import React, { useState, useEffect } from 'react';

const ShareLink = ({ roomId }) => {
  const [shareLink, setShareLink] = useState('');
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showQr, setShowQr] = useState(false);

  useEffect(() => {
    if (roomId) {
      generateShareLink();
    }
  }, [roomId]);

  // Generate shareable link from the API
  const generateShareLink = async () => {
    if (!roomId) return;

    setLoading(true);
    setError(null);

    try {
      // Get the current window location to build the share link manually
      // This is a fallback in case the API call fails
      const origin = window.location.origin;
      const fallbackShareLink = `${origin}/join/${roomId}`;
      const fallbackQrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(fallbackShareLink)}`;

      try {
        const response = await fetch(`/api/rooms/${roomId}/share-link`);

        if (!response.ok) {
          console.warn('API request failed, using fallback share link');
          setShareLink(fallbackShareLink);
          setQrCodeUrl(fallbackQrUrl);
        } else {
          const data = await response.json();
          setShareLink(data.share_link);
          setQrCodeUrl(data.qr_code_url || fallbackQrUrl);
        }
      } catch (err) {
        console.warn('API request failed, using fallback share link:', err);
        setShareLink(fallbackShareLink);
        setQrCodeUrl(fallbackQrUrl);
      }
    } catch (err) {
      setError('Failed to generate share link. Please try again.');
      console.error('Error generating share link:', err);
    } finally {
      setLoading(false);
    }
  };

  // Copy link to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(shareLink)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(err => {
        console.error('Failed to copy:', err);
        setError('Failed to copy to clipboard');
      });
  };

  // Toggle QR code display
  const toggleQrCode = () => {
    setShowQr(!showQr);
  };

  return (
    <div className="share-link-container">
      <h3>Invite Others</h3>
      {loading ? (
        <p>Generating link...</p>
      ) : error ? (
        <p className="error">{error}</p>
      ) : (
        <div className="share-link-content">
          <div className="link-section">
            <input
              type="text"
              value={shareLink}
              readOnly
              className="share-link-input"
            />
            <button
              onClick={copyToClipboard}
              className="copy-btn"
              disabled={!shareLink}
              aria-label="Copy link to clipboard"
            >
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>

          <div className="share-options">
            <button
              onClick={toggleQrCode}
              className="qr-toggle-btn"
              aria-label="Toggle QR code"
            >
              {showQr ? 'Hide QR Code' : 'Show QR Code'}
            </button>

            {shareLink && (
              <a
                href={`mailto:?subject=Join%20my%20CZone%20meeting&body=Join%20my%20video%20conference%20at%3A%20${encodeURIComponent(shareLink)}`}
                className="share-email-btn"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Share via email"
              >
                Email Link
              </a>
            )}
          </div>

          {showQr && qrCodeUrl && (
            <div className="qr-code-container">
              <p className="qr-instruction">Scan this QR code to join the meeting:</p>
              <img
                src={qrCodeUrl}
                alt="QR code to join meeting"
                className="qr-code-image"
              />
              <p className="security-note">
                <strong>Note:</strong> For secure video conferencing in production,
                this link should use HTTPS and be accessible from the public internet.
              </p>
            </div>
          )}
        </div>
      )}
      <p className="share-link-help" style={{ fontSize: '0.8rem', marginTop: '0.25rem', color: '#666' }}>
        Share this link to invite others
      </p>
    </div>
  );
};

export default ShareLink;
